/**
 * Vault risk bands + status colors — single source of truth for SPA UI.
 *
 * Aligned with protocol vault-manager:
 *   isLiquidatable := CR < liquidationThreshold  (gov-tunable, default 1.30×)
 *   isUnderwater   := CV < debt                  (structural CR < 1.00×)
 *   mint floor     := minCollateralRatio         (gov-tunable, default 1.50×)
 *
 * UI bands (health factor = CR):
 *   Healthy      green  — CR ≥ cautionFloor (mint × CAUTION_CR_MULT)
 *   Caution      yellow — mint ≤ CR < cautionFloor
 *   At risk      orange — liqT ≤ CR < mint (1.50 → 1.30)
 *   Liquidatable red    — 1.00 ≤ CR < liqT
 *   Underwater   purple — CR < 1.00
 *
 * Never hardcode band edges in components — pass live getProtocolParams.
 */

/** Protocol defaults (amounts.mo) — offline / loading only */
export const FALLBACK_MIN_COLLATERAL_RATIO = 1.5;
export const FALLBACK_LIQUIDATION_THRESHOLD = 1.3;

/** Healthy when CR ≥ mintFloor × this (Caution sits between mint and this). */
export const CAUTION_CR_MULT = 1.1;

export type ProtocolRiskParams = {
	/** Mint floor / healthy min CR (minCollateralRatio) */
	minCollateralRatio: number;
	/** Soft-band liquidate when CR below this (liquidationThreshold) */
	liquidationThreshold: number;
};

export const FALLBACK_RISK_PARAMS: ProtocolRiskParams = {
	minCollateralRatio: FALLBACK_MIN_COLLATERAL_RATIO,
	liquidationThreshold: FALLBACK_LIQUIDATION_THRESHOLD,
};

export type VaultRiskBand =
	| "healthy"
	| "caution"
	| "at_risk"
	| "liquidatable"
	| "underwater"
	| "empty";

/** Hex palette for gauges / inline styles (light + dark UI). */
export const RISK_STATUS_COLORS = {
	healthy: "#059669",
	caution: "#EAB308",
	at_risk: "#F97316",
	liquidatable: "#DC2626",
	underwater: "#9333EA",
	empty: "#718096",
} as const;

/** Tailwind surface classes shared by table rows, grid cards, detail, liquidation. */
export const RISK_STATUS_CLASSES: Record<
	VaultRiskBand,
	{
		badgeClass: string;
		borderClass: string;
		factorClass: string;
		bgClass: string;
		ringClass: string;
	}
> = {
	healthy: {
		badgeClass:
			"bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
		borderClass: "border-emerald-500/40",
		factorClass: "text-emerald-600 dark:text-emerald-400",
		bgClass: "bg-emerald-500/5",
		ringClass: "ring-1 ring-emerald-500/35 bg-emerald-500/5",
	},
	caution: {
		badgeClass: "bg-yellow-500/20 text-yellow-800 dark:text-yellow-300",
		borderClass: "border-yellow-500/45",
		factorClass: "text-yellow-600 dark:text-yellow-400",
		bgClass: "bg-yellow-500/10",
		ringClass: "ring-1 ring-yellow-500/40 bg-yellow-500/5",
	},
	at_risk: {
		badgeClass: "bg-orange-500/20 text-orange-800 dark:text-orange-300",
		borderClass: "border-orange-500/45",
		factorClass: "text-orange-600 dark:text-orange-400",
		bgClass: "bg-orange-500/10",
		ringClass: "ring-2 ring-orange-500/45 bg-orange-500/10",
	},
	liquidatable: {
		badgeClass: "bg-red-600/20 text-red-700 dark:text-red-300",
		borderClass: "border-red-500/50",
		factorClass: "text-red-600 dark:text-red-400",
		bgClass: "bg-red-500/10",
		ringClass: "ring-2 ring-red-500/50 bg-red-500/10",
	},
	underwater: {
		badgeClass: "bg-purple-600/20 text-purple-800 dark:text-purple-300",
		borderClass: "border-purple-500/50",
		factorClass: "text-purple-600 dark:text-purple-400",
		bgClass: "bg-purple-500/15",
		ringClass: "ring-2 ring-purple-500/50 bg-purple-500/10",
	},
	empty: {
		badgeClass: "bg-muted text-muted-foreground",
		borderClass: "border-border",
		factorClass: "text-muted-foreground",
		bgClass: "bg-muted/40",
		ringClass: "",
	},
};

export type VaultRiskMeta = {
	band: VaultRiskBand;
	/** CR as multiplier, e.g. 1.42 */
	cr: number;
	label: string;
	badge: string;
	badgeClass: string;
	borderClass: string;
	factorClass: string;
	bgClass: string;
	ringClass: string;
	color: string;
	hint: string;
};

export function crFromUsd(collateralUsd: number, debtUsdm: number): number {
	if (!(debtUsdm > 0)) return Number.POSITIVE_INFINITY;
	if (!(collateralUsd >= 0)) return 0;
	return collateralUsd / debtUsdm;
}

export function normalizeRiskParams(
	params?: Partial<ProtocolRiskParams> | null,
): ProtocolRiskParams {
	const minCr =
		params?.minCollateralRatio && params.minCollateralRatio > 0
			? params.minCollateralRatio
			: FALLBACK_MIN_COLLATERAL_RATIO;
	const liqT =
		params?.liquidationThreshold && params.liquidationThreshold > 0
			? params.liquidationThreshold
			: FALLBACK_LIQUIDATION_THRESHOLD;
	const liquidationThreshold = Math.min(liqT, minCr);
	const minCollateralRatio = Math.max(minCr, liquidationThreshold);
	return { minCollateralRatio, liquidationThreshold };
}

function metaFor(
	band: VaultRiskBand,
	cr: number,
	label: string,
	badge: string,
	hint: string,
): VaultRiskMeta {
	const c = RISK_STATUS_CLASSES[band];
	return {
		band,
		cr,
		label,
		badge,
		badgeClass: c.badgeClass,
		borderClass: c.borderClass,
		factorClass: c.factorClass,
		bgClass: c.bgClass,
		ringClass: c.ringClass,
		color: RISK_STATUS_COLORS[band],
		hint,
	};
}

/**
 * Bands from live protocol params:
 * - Healthy: CR ≥ mint × CAUTION_CR_MULT
 * - Caution: mint ≤ CR < mint × CAUTION_CR_MULT
 * - At risk: liquidationThreshold ≤ CR < mint
 * - Liquidatable (fixed-spread): 1.00× ≤ CR < liquidationThreshold
 * - Underwater (Dutch): CV < debt (CR < 1.00×)
 */
export function classifyVaultRisk(
	collateralUsd: number,
	debtUsdm: number,
	params?: Partial<ProtocolRiskParams> | null,
): VaultRiskMeta {
	const { minCollateralRatio: minCr, liquidationThreshold: liqT } =
		normalizeRiskParams(params);
	const cautionFloor = minCr * CAUTION_CR_MULT;

	if (!(debtUsdm > 0) && !(collateralUsd > 0)) {
		return metaFor(
			"empty",
			Number.POSITIVE_INFINITY,
			"Empty",
			"Empty",
			"No debt or collateral.",
		);
	}
	const cr = crFromUsd(collateralUsd, debtUsdm);
	if (!Number.isFinite(cr)) {
		return metaFor(
			"healthy",
			Number.POSITIVE_INFINITY,
			"Healthy",
			"Healthy",
			"No debt — fully collateralized.",
		);
	}
	if (collateralUsd < debtUsdm) {
		return metaFor(
			"underwater",
			cr,
			"Underwater",
			"UNDERWATER · Dutch",
			"Collateral value below debt — Dutch auction path only.",
		);
	}
	if (cr < liqT) {
		return metaFor(
			"liquidatable",
			cr,
			"Liquidatable",
			"LIQUIDATABLE",
			`CR below ${liqT.toFixed(2)}× liquidation threshold — fixed-spread open.`,
		);
	}
	if (cr < minCr) {
		return metaFor(
			"at_risk",
			cr,
			"At risk",
			"At risk",
			`Above ${liqT.toFixed(2)}× liq line but below ${minCr.toFixed(2)}× min CR — add collateral or repay.`,
		);
	}
	if (cr < cautionFloor) {
		return metaFor(
			"caution",
			cr,
			"Caution",
			"Caution",
			`Near mint floor ${minCr.toFixed(2)}× — watch before minting more.`,
		);
	}
	return metaFor(
		"healthy",
		cr,
		"Healthy",
		"Healthy",
		`At or above ${cautionFloor.toFixed(2)}× (mint floor ${minCr.toFixed(2)}× + buffer).`,
	);
}

export function formatCr(cr: number, digits = 2): string {
	if (!Number.isFinite(cr)) return "∞";
	return `${cr.toFixed(digits)}×`;
}

/** Dynamic legend from live params (Vaults / Liquidate pages). */
export function riskLegend(params?: Partial<ProtocolRiskParams> | null) {
	const { minCollateralRatio: minCr, liquidationThreshold: liqT } =
		normalizeRiskParams(params);
	const cautionFloor = minCr * CAUTION_CR_MULT;
	return [
		{
			band: "healthy" as const,
			badge: "Healthy",
			range: `CR ≥ ${cautionFloor.toFixed(2)}×`,
			color: RISK_STATUS_COLORS.healthy,
			badgeClass: RISK_STATUS_CLASSES.healthy.badgeClass,
		},
		{
			band: "caution" as const,
			badge: "Caution",
			range: `${minCr.toFixed(2)}× ≤ CR < ${cautionFloor.toFixed(2)}×`,
			color: RISK_STATUS_COLORS.caution,
			badgeClass: RISK_STATUS_CLASSES.caution.badgeClass,
		},
		{
			band: "at_risk" as const,
			badge: "At risk",
			range: `${liqT.toFixed(2)}× ≤ CR < ${minCr.toFixed(2)}×`,
			color: RISK_STATUS_COLORS.at_risk,
			badgeClass: RISK_STATUS_CLASSES.at_risk.badgeClass,
		},
		{
			band: "liquidatable" as const,
			badge: "LIQUIDATABLE",
			range: `1.00× ≤ CR < ${liqT.toFixed(2)}× · fixed-spread`,
			color: RISK_STATUS_COLORS.liquidatable,
			badgeClass: RISK_STATUS_CLASSES.liquidatable.badgeClass,
		},
		{
			band: "underwater" as const,
			badge: "UNDERWATER · Dutch",
			range: "CR < 1.00× · Dutch auction",
			color: RISK_STATUS_COLORS.underwater,
			badgeClass: RISK_STATUS_CLASSES.underwater.badgeClass,
		},
	] as const;
}

/** Row / card wash from band (table + grid). */
export function rowBgForBand(band: VaultRiskBand | string): string {
	const key = band as VaultRiskBand;
	return RISK_STATUS_CLASSES[key]?.bgClass ?? "";
}

/**
 * Strictest thresholds among assets held (mirrors Motoko
 * strictestMinRatioE8 / strictestLiquidationThresholdE8).
 */
export function strictestRiskParams(
	global: ProtocolRiskParams,
	assetParams: Array<{
		asset: string;
		minCollateralRatio?: number;
		liquidationThreshold?: number;
	}>,
	heldAssets: string[],
): ProtocolRiskParams {
	if (!heldAssets.length || !assetParams.length) return global;
	let minCr = 0;
	let liqT = 0;
	for (const asset of heldAssets) {
		const p = assetParams.find((x) => x.asset === asset);
		const m = p?.minCollateralRatio ?? global.minCollateralRatio;
		const t = p?.liquidationThreshold ?? global.liquidationThreshold;
		if (m > minCr) minCr = m;
		if (t > liqT) liqT = t;
	}
	if (minCr <= 0) minCr = global.minCollateralRatio;
	if (liqT <= 0) liqT = global.liquidationThreshold;
	return normalizeRiskParams({
		minCollateralRatio: minCr,
		liquidationThreshold: liqT,
	});
}
